import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from "react-native";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";

import { Observer, observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "./../_layout";
import { useTranslation } from "react-i18next";

import { AntDesign, FontAwesome6, Ionicons } from "@expo/vector-icons";

import { Menu, MenuItem } from "react-native-material-menu";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Feather } from "@expo/vector-icons";
import { reaction, runInAction } from "mobx";
import Serie from "../Firestore/Serie";
import CustomAlert from "../Components/CustomAlert";
import { ProtocolCases, ProtocolTitles } from "../../Resources/Constants";
import { formatNumber1Dec, formatNumber2Dec } from "../utils/numberUtils";
import { ScrollView } from "react-native-web";
import MediaManager from "../Classes/MediaManager";

import PulseSkeleton from "../Components/PulseSkeleton";
import FloatingExerciseLink from "../Components/FloatingExerciseLink";
import Tooltip from "../Components/Tooltip";
import TypingAnimation from "../Components/TypingAnimation";
import SlotTextNew from "./SlotTextNew";

const ProtocolRowTitleToggle = observer(
  ({ block, protocolTitle, calendar }) => {
    const { t } = useTranslation();
    const { isMobile } = useDevice();
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "flex-start",
          paddingHorizontal: 8,
          paddingVertical: 2,
          flexDirection: "row",
          outlineStyle: "none",
        }}
        onPress={() => {
          const prevState = block.getProtocolSeriesShown(protocolTitle);
          block.toggleProtocolSeriesShown(protocolTitle);

          if (calendar && block.client) {
            const macroBlocks = block.client.blocks.filter(
              (b) => b.label === block.label && b.id != block.id
            );
            macroBlocks.forEach((b) => {
              if (b.getProtocolSeriesShown(protocolTitle) === prevState) {
                b.toggleProtocolSeriesShown(protocolTitle);
              }
            });
          }
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Feather
          name={
            block.getProtocolSeriesShown(protocolTitle)
              ? "chevron-up"
              : "chevron-down"
          }
          size={16}
          color={Colors.bubbleColor}
          style={{ marginRight: 4 }}
        />
        <Text
          style={{
            textAlign: "left",
            fontSize: 12,
            fontWeight: "500",
            color: isHovered ? Colors.darkgrayColor : Colors.grayColor,
            flex: 1,
            transition: "color 200ms ease",
          }}
          numberOfLines={2}
        >
          {t(protocolTitle)}
        </Text>
      </TouchableOpacity>
    );
  }
);

const ProtocolRow = ({
  block,
  protocolTitle,
  calendar = false,
  visibleRange,
  columnWidth,
  showSetPriority = true,
}) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  // Pending focus requests for protocol text inputs after exercise selection.
  // Map<key, { wasEditing: boolean }> – keyed by `${protocolTitle}:${week}:${isSecondary}`.
  // Lives at ProtocolRow level so it survives SlotOfWeek remounts.
  const protocolTextFocusRequestsRef = useRef(new Map());

  // Tracks current edit-mode state so we can snapshot it when a focus request is made.
  const protocolTextEditModesRef = useRef(new Set());

  // Bump to trigger focus effects (refs alone don't cause re-renders).
  const [focusRequestVersion, setFocusRequestVersion] = useState(0);

  const getSlotColor = (protocolSlot) => {
    // 1. Strip the "-string" suffix
    const raw = protocolSlot.replace(/-string$/, "");
    // 2. Find the first uppercase letter to split priority vs. type
    const idx = raw.search(/[A-Z]/);
    const priority = idx !== -1 ? raw.substring(0, idx).toLowerCase() : "";

    // 3. Return the color based on priority
    switch (priority) {
      case "primary":
        return "rgba(255, 0, 0, 1)"; // Red
      case "secondary":
        return "rgba(63, 123, 141, 1)"; // Blue‐gray
      case "tertiary":
        return "rgba(34, 139, 34, 1)"; // Green
      case "quaternary":
        return "rgba(255, 165, 0, 1)"; // Orange
      case "quinary":
        return "rgba(255, 192, 203, 1)"; // Pink
      default:
        return "rgba(0, 0, 0, 0.1)"; // Fallback light gray
    }
  };

  const SlotOfWeekSkeleton = ({}) => {
    return (
      <View
        style={{
          alignItems: "center",
          width: "100%",
          flexDirection: "row",
          flex: 1,
        }}
      >
        <View
          style={{
            width: "50%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PulseSkeleton width={120} height={12} />
        </View>
        <View
          style={{
            width: "50%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PulseSkeleton width={120} height={28} />
        </View>
      </View>
    );
  };

  const SlotOfWeek = observer(({ slotExercise, week }) => {
    const useCTAColors =
      week === 1 &&
      (protocolTitle === "primarySquat-string" ||
        protocolTitle === "primaryBenchPress-string" ||
        protocolTitle === "primaryDeadlift-string");

    const [showingExerciseWithDataWarning, setShowingExerciseWithDataWarning] =
      useState(false);

    const openPrimarySearchRef = useRef(null);

    // Define selectSlot at SlotOfWeek level so both SlotText and SlotTextNew can use it
    const selectSlot = (slotExercise, isSecondaryProtocol) => {
      if (slotExercise) {
        if (isSecondaryProtocol) {
          block.setProperty(
            "selectedSlotId",
            slotExercise.getSecondProtocol.id.toLowerCase()
          );
        } else {
          block.setProperty("selectedSlotId", slotExercise.id.toLowerCase());
        }
      } else {
        block.setProperty("selectedSlotId", null);
        block.setProperty("isProtocolAttributesShowing", false);
        block.setProperty("selectedProtocolSlot", protocolTitle);
        block.setProperty("selectedProtocolWeek", week);
      }
      if (calendar && block.client) {
        block.client.setProperty("focusedBlock", block);
      }
      block.setProperty("showingInfoView", true);
    };

    //Old method. Keep for reference. Delete in two weeks.
    const SlotText = observer(({}) => {
      const [isHovered, setIsHovered] = useState(false);
      const [isPlusHovered, setIsPlusHovered] = useState(false);

      return (
        <View
          style={{
            alignItems: "center",
            flex: 1,
          }}
          onMouseEnter={() => {
            if (!isMobile) {
              setIsHovered(true);
            }
          }}
          onMouseLeave={() => setIsHovered(false)} // Hide tooltip when not hovering
        >
          {slotExercise ? (
            <>
              <Text
                numberOfLines={2}
                onPress={() => selectSlot(slotExercise)}
                style={{
                  width: "100%",
                  color: "black",
                  textAlign: "center",
                  fontSize: screenSize === "small" ? 11 : 11,
                  padding: 2,
                }}
              >
                {slotExercise.getName(t, i18n.language)}
              </Text>
              {slotExercise.getSecondProtocol && (
                <>
                  <Text
                    style={{
                      width: "100%",
                      color: "black",
                      textAlign: "center",
                      fontSize: screenSize === "small" ? 11 : 11,
                      padding: 2,
                    }}
                  >
                    +
                  </Text>
                  <Text
                    numberOfLines={2}
                    onPress={() => selectSlot(slotExercise, true)}
                    style={{
                      width: "100%",
                      color: "black",
                      textAlign: "center",
                      fontSize: screenSize === "small" ? 11 : 11,
                      padding: 2,
                    }}
                  >
                    {slotExercise.getSecondProtocol.getName(t, i18n.language)}
                  </Text>
                </>
              )}

              {(isHovered || isMobile) && !slotExercise.getSecondProtocol && (
                <TouchableOpacity
                  onPress={() => {
                    if (calendar && block.client) {
                      block.client.setProperty("focusedBlock", block);
                    }
                    slotExercise.createSecondProtocol();
                    block.setProperty("isProtocolAttributesShowing", false);
                    setIsHovered(false);
                  }}
                  style={{
                    width: "50%",
                    alignItems: "center",
                    backgroundColor: Colors.lightGrayColor,
                    borderRadius: 10,
                    marginTop: 5,
                    outlineStyle: "none",
                  }}
                >
                  <AntDesign name="plus" size={8} color={Colors.blackColor} />
                </TouchableOpacity>
              )}
            </>
          ) : (
            <TouchableOpacity
              onPress={() => selectSlot(null)}
              style={{
                width: "100%",
                maxWidth: 128,
                opacity: isPlusHovered ? 0.5 : 1,
                outlineStyle: "none",
              }}
              onMouseEnter={() => !isMobile && setIsPlusHovered(true)}
              onMouseLeave={() => setIsPlusHovered(false)}
            >
              <View
                style={{
                  width: "100%",
                  alignItems: "center",
                  backgroundColor: useCTAColors
                    ? Colors.bubbleColor
                    : Colors.lightGrayColor,
                  borderRadius: 10,
                }}
              >
                <AntDesign
                  name="plus"
                  size={10}
                  color={useCTAColors ? Colors.whiteColor : Colors.blackColor}
                />
              </View>
            </TouchableOpacity>
          )}
        </View>
      );
    });

    const ProtocolText = observer(({}) => {
      const [showingProtocolDetectionAlert, setShowingProtocolDetectionAlert] =
        useState(false);

      const ProtocolTextBox = observer(({ slotExercise }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [showingEdit, setShowingEdit] = useState(false);
        const [shouldFocusAtEnd, setShouldFocusAtEnd] = useState(false);
        const textInputRef = useRef(null);

        const isSecondary = slotExercise?.isSecondaryProtocol ? 1 : 0;
        const protocolTextKey = `${protocolTitle}:${week}:${isSecondary}`;

        // Initialize edit mode based on whether protocol text is empty.
        useEffect(() => {
          setShowingEdit(slotExercise.protocolText === "");
        }, []);

        // Keep edit-mode ref in sync so we can snapshot it when focus is requested.
        useEffect(() => {
          const modes = protocolTextEditModesRef.current;
          showingEdit
            ? modes.add(protocolTextKey)
            : modes.delete(protocolTextKey);
          return () => modes.delete(protocolTextKey);
        }, [showingEdit, protocolTextKey]);

        // Perform focus when shouldFocusAtEnd is set.
        useEffect(() => {
          if (showingEdit && shouldFocusAtEnd && textInputRef.current) {
            setTimeout(() => {
              textInputRef.current?.focus();
              setShouldFocusAtEnd(false);
            }, 100);
          }
        }, [showingEdit, shouldFocusAtEnd]);

        // Handle pending focus request after exercise selection.
        useEffect(() => {
          const request =
            protocolTextFocusRequestsRef.current.get(protocolTextKey);
          if (!request) return;

          // Consume request.
          protocolTextFocusRequestsRef.current.delete(protocolTextKey);

          // Focus if already editing, or enter edit+focus if empty/was editing.
          if (
            showingEdit ||
            slotExercise?.protocolText === "" ||
            request.wasEditing
          ) {
            setShowingEdit(true);
            setShouldFocusAtEnd(true);
          }
        }, [
          focusRequestVersion,
          protocolTextKey,
          showingEdit,
          slotExercise?.protocolText,
        ]);

        const changeProtocol = (text, slotExercise) => {
          //Check if any of the series have values in actual
          const actualWithData = slotExercise.series.some(
            (s) => s.weight > 0 || s.rpe > 0 || s.reps > 0
          );

          if (
            actualWithData &&
            !slotExercise.ignoreActualWithDataWarning &&
            slotExercise.workout
          ) {
            slotExercise.setProperty("showActualWithDataWarning", true);
            return;
          }
          slotExercise.setProperty("protocolText", text);

          runInAction(() => {
            updateSlotExerciseWithProtocol(slotExercise);
          });
        };

        const addSuggestion = (suggestion) => {
          slotExercise.setProperty(
            "protocolText",
            slotExercise.protocolText + suggestion
          );
          runInAction(() => {
            updateSlotExerciseWithProtocol(slotExercise);
          });
        };

        const matchTest = (protocolCase, trimmedText) => {
          // Normalize by stripping all whitespace and lowercasing
          const normalized = trimmedText.replace(/\s+/g, "").toLowerCase();

          switch (protocolCase) {
            case ProtocolCases.RPE_PROGRESSION:
              // matches "x3@4-4-5"
              return /^x\d+@(\d+(\.\d+)?)(-\d+(\.\d+)?)+$/.test(normalized);
            case ProtocolCases.WEIGHT_PROGRESSION:
              // matches "175-180-185x3"
              return /^\d+(-\d+)+x\d+$/.test(normalized);
            case ProtocolCases.RAMP:
              // matches "ramp3x4@8"
              return /^ramp\d+x\d+@\d+(\.\d+)?$/.test(normalized);
            // case ProtocolCases.WEIGHT_REPS:
            //   // matches "200x4"
            //   return /^\d+x\d+$/.test(normalized);
            case ProtocolCases.SETS_REPS_RPE:
              // matches "3x4@5"
              return /^\d+x\d+@\d+(\.\d+)?$/.test(normalized);
            case ProtocolCases.SETS_REPS_WEIGHT:
              // matches "<sets>x<reps>x<weight>", "3x5x145"
              // return /^\d+x\d+x\d+$/.test(normalized);
              return /^\d+x\d+x\d+(?:\.\d+)?$/.test(normalized);
            case ProtocolCases.REPS_AND_RPE:
              // matches "x4@5"
              return /^x\d+@\d+(\.\d+)?$/.test(normalized);
            case ProtocolCases.SINGLE:
              // matches "single@5"
              return /^single@\d+(\.\d+)?$/.test(normalized);
            default:
              return false;
          }
        };

        const getSuggestion = (input) => {
          if (!input) {
            return "";
          }
          // Split out the last segment after any "+"
          const parts = input.split("+");
          const lastSegment = parts[parts.length - 1].trim();
          // Collapse any internal whitespace to single spaces for pattern matching
          const collapsedSegment = lastSegment.replace(/\s+/g, " ");
          // Suggest weight progression until three values are entered, then suggest reps
          // Suggest weight progression once a dash is typed after a number
          const weightProgressMatch = lastSegment.match(
            /^(\d+)(?:-(?:\d*)?)+$/
          );
          if (weightProgressMatch) {
            // console.log("weightProgressMatch");
            const baseVal = weightProgressMatch[1];
            const additionalMatches = lastSegment.match(/-(\d+)/g) || [];
            const weightVal =
              additionalMatches.length > 0
                ? additionalMatches[additionalMatches.length - 1].slice(1)
                : baseVal;
            const typedCount = 1 + additionalMatches.length;
            const missing = 3 - typedCount;
            if (missing > 0) {
              const fill = Array(missing).fill(weightVal).join("-");
              // If user just typed a dash, append all remaining without leading dash
              if (lastSegment.endsWith("-")) {
                return fill + " x4";
              }
              // Otherwise prefix with a dash to complete all remaining
              return "-" + fill + " x4";
            } else {
              // All weight values entered, suggest reps
              return " x4";
            }
          }
          // Trigger RPE progression only once a dash is present (e.g. x3@4-, x3@4-5)
          const rpeProgressMatch = lastSegment.match(
            /^x(\d+)\s*@(\d+)(?:-(?:\d+)?)+$/i
          );
          if (rpeProgressMatch) {
            const baseVal = rpeProgressMatch[2];
            // Count how many RPE values have been typed (initial plus subsequent)
            const additionalMatches = lastSegment.match(/-(\d+)/g) || [];
            // Choose the last typed RPE if available, otherwise use the base RPE
            const rpeVal =
              additionalMatches.length > 0
                ? additionalMatches[additionalMatches.length - 1].slice(1)
                : baseVal;
            const typedCount = 1 + additionalMatches.length;
            const missing = 3 - typedCount;
            if (missing > 0) {
              const fill = Array(missing).fill(rpeVal).join("-");
              // When user ends with a dash, append all remaining without leading dash
              if (lastSegment.endsWith("-")) {
                return fill;
              }
              // Otherwise prefix with a dash to complete all remaining
              return "-" + fill;
            }
          }
          // If user types "<sets>x<reps>x", suggest default weight "100"
          const srwDashMatch = lastSegment.match(/^(\d+)x(\d+)x$/i);
          if (srwDashMatch) {
            return "100";
          }
          // If the last segment matches any protocol case, suggest "+" to chain
          // find the first case that really matches
          const matchedCase = Object.values(ProtocolCases).find((pc) =>
            matchTest(pc, lastSegment)
          );
          if (matchedCase) {
            // console.log("matched protocol case:", matchedCase);
            return " + ";
          }
          const cases = [
            //Case Ramp
            { regex: /^r$/, suggestion: "amp 3x4 @7" },
            { regex: /^ra$/, suggestion: "mp 3x4 @7" },
            { regex: /^ram$/, suggestion: "p 3x4 @7" },
            { regex: /^ramp$/, suggestion: " 3x4 @7" },
            { regex: /^ramp\s*$/, suggestion: "3x4 @7" },
            { regex: /^ramp\s*\d$/, suggestion: "x4 @7" },
            { regex: /^ramp\s*\dx$/, suggestion: "4 @7" },
            { regex: /^ramp\s*\dx\d$/, suggestion: " @7" },
            { regex: /^ramp\s*\dx\d\s*$/, suggestion: "@7" },
            { regex: /^ramp\s*\dx\d\s*@$/, suggestion: "7" },
            //Case SERIESxREPS @RPE
            { regex: /^\d$/, suggestion: "x4 @5" },
            { regex: /^\dx$/, suggestion: "4 @5" },
            { regex: /^\dx\d$/, suggestion: " @5" },
            { regex: /^\dx\d\s*$/, suggestion: "@5" },
            { regex: /^\dx\d\s*@$/, suggestion: "5" },
            //Case SINGLE @RPE
            { regex: /^s$/, suggestion: "ingle @5" },
            { regex: /^si$/, suggestion: "ngle @5" },
            { regex: /^sin$/, suggestion: "gle @5" },
            { regex: /^sing$/, suggestion: "le @5" },
            { regex: /^singl$/, suggestion: "e @5" },
            { regex: /^single$/, suggestion: " @5" },
            { regex: /^single\s*$/, suggestion: "@5" },
            { regex: /^single\s*@$/, suggestion: "5" },
            //Case xREPS @RPE
            { regex: /^x$/, suggestion: "4 @5" },
            { regex: /^x\d$/, suggestion: " @5" },
            { regex: /^x\d\s*$/, suggestion: "@5" },
            { regex: /^x\d\s*@$/, suggestion: "5" },
            //Case + RAMP
            { regex: /\+\s*r$/, suggestion: "amp 3x4 @4" },
            { regex: /\+\s*ra$/, suggestion: "mp 3x4 @4" },
            { regex: /\+\s*ram$/, suggestion: "p 3x4 @4" },
            { regex: /\+\s*ramp$/, suggestion: " 3x4 @4" },
            { regex: /\+\s*ramp\s*$/, suggestion: "3x4 @4" },
            { regex: /\+\s*ramp\s*\d$/, suggestion: "x4 @4" },
            { regex: /\+\s*ramp\s*\dx$/, suggestion: "4 @4" },
            { regex: /\+\s*ramp\s*\dx\d$/, suggestion: " @4" },
            { regex: /\+\s*ramp\s*\dx\d\s*$/, suggestion: "@4" },
            { regex: /\+\s*ramp\s*\dx\d\s*@$/, suggestion: "4" },
            //Case + SERIESxREPS @RPE
            { regex: /\+$/, suggestion: " 3x4 @5" },
            { regex: /\+\s*$/, suggestion: "3x4 @5" },
            { regex: /\+\s*\d$/, suggestion: "x4 @5" },
            { regex: /\+\s*\dx$/, suggestion: "4 @5" },
            { regex: /\+\s*\dx\d$/, suggestion: " @5" },
            { regex: /\+\s*\dx\d\s*$/, suggestion: "@5" },
            { regex: /\+\s*\dx\d\s*@$/, suggestion: "5" },
            //Case + SINGLE @RPE
            { regex: /\+\s*s$/, suggestion: "ingle @5" },
            { regex: /\+\s*si$/, suggestion: "ngle @5" },
            { regex: /\+\s*sin$/, suggestion: "gle @5" },
            { regex: /\+\s*sing$/, suggestion: "le @5" },
            { regex: /\+\s*singl$/, suggestion: "e @5" },
            { regex: /\+\s*single$/, suggestion: " @5" },
            { regex: /\+\s*single\s*$/, suggestion: "@5" },
            { regex: /\+\s*single\s*@$/, suggestion: "5" },

            // Case RPE progression (e.g. "x3@4-4-3")
            // { regex: /^x\d+@$/, suggestion: "5-6-6" },
            // {
            //   regex: /^x\d+@(\d+(?:\.\d+)?)(-\d+(\.\d+)?)+\s$/,
            //   suggestion: "",
            // },
            // Case weight progression (e.g. "120-125x6")
            { regex: /^\d+(?:-\d+)+$/, suggestion: " x4" },
            { regex: /^\d+(?:-\d+)+\s*$/, suggestion: "x4" },
            { regex: /^\d+(?:-\d+)+\s*x$/, suggestion: "4" },
            //Case + xREPS @RPE
            { regex: /\+\s*x$/, suggestion: "4 @5" },
            { regex: /\+\s*x\d$/, suggestion: " @5" },
            { regex: /\+\s*x\d\s*$/, suggestion: "@5" },
            { regex: /\+\s*x\d\s*@$/, suggestion: "5" },
          ];

          for (let { regex, suggestion } of cases) {
            if (regex.test(collapsedSegment.toLowerCase())) {
              return suggestion;
            }
          }

          return "";
        };

        const getProtocolTextError = (text) => {
          if (!text) return false;
          // Split on '+', trim segments, ignore empty
          const parts = text
            .split("+")
            .map((p) => p.trim())
            .filter((p) => p);
          // If any segment does not match any protocol case, flag error
          return !parts.every((part) =>
            Object.values(ProtocolCases).some((pc) => matchTest(pc, part))
          );
        };
        // Compute error status only when the protocol text actually changes
        const protocolTextError = useMemo(
          () => getProtocolTextError(slotExercise.protocolText),
          [slotExercise.protocolText]
        );
        const suggestion = getSuggestion(slotExercise.protocolText);
        const updateSlotExerciseWithProtocol = (slotExercise) => {
          // Helper to process chained protocol segments
          const processChainedSegment = (slotExercise, segment) => {
            if (matchTest(ProtocolCases.RAMP, segment)) {
              updateExercise(slotExercise, ProtocolCases.RAMP, segment, true);
            } else if (matchTest(ProtocolCases.RPE_PROGRESSION, segment)) {
              updateExercise(
                slotExercise,
                ProtocolCases.RPE_PROGRESSION,
                segment,
                true
              );
            } else if (matchTest(ProtocolCases.WEIGHT_PROGRESSION, segment)) {
              updateExercise(
                slotExercise,
                ProtocolCases.WEIGHT_PROGRESSION,
                segment,
                true
              );
            } else if (matchTest(ProtocolCases.SETS_REPS_RPE, segment)) {
              updateExercise(
                slotExercise,
                ProtocolCases.SETS_REPS_RPE,
                segment,
                true
              );
            } else if (matchTest(ProtocolCases.SETS_REPS_WEIGHT, segment)) {
              updateExercise(
                slotExercise,
                ProtocolCases.SETS_REPS_WEIGHT,
                segment,
                true
              );
            } else if (matchTest(ProtocolCases.WEIGHT_REPS, segment)) {
              updateExercise(
                slotExercise,
                ProtocolCases.WEIGHT_REPS,
                segment,
                true
              );
            } else if (matchTest(ProtocolCases.REPS_AND_RPE, segment)) {
              updateExercise(
                slotExercise,
                ProtocolCases.REPS_AND_RPE,
                segment,
                true
              );
            } else if (matchTest(ProtocolCases.SINGLE, segment)) {
              updateExercise(slotExercise, ProtocolCases.SINGLE, segment, true);
            } else {
              console.warn("Unknown case after '+':", segment);
            }
          };
          const text = slotExercise.protocolText;

          let trimmedText = text.trim();

          if (text.includes("+")) {
            trimmedText = text.split("+")[0].trim();
          }

          const updateExercise = (
            slotExercise,
            protocolCase,
            trimmedText,
            isPart = false
          ) => {
            if (!isPart) {
              // Replace only the first N entries in protocolSeries, where N is the new series count
              const oldSeries = slotExercise.protocolSeries || [];
              const updatedCount = slotExercise.series.length;
              const newProtocolSeries = oldSeries.slice();
              for (let i = 0; i < updatedCount; i++) {
                newProtocolSeries[i] = slotExercise.series[i];
              }
              //Check if there are all empty and do not protocolSeries if so
              const defaultSeries = slotExercise.series.every(
                (serie) =>
                  serie.relevance === 3 &&
                  !serie.requireVideo &&
                  serie.targetWeight === "" &&
                  serie.targetRpe === "" &&
                  serie.targetReps === ""
              );
              if (!defaultSeries) {
                // console.log("Updating protocolSeries:", newProtocolSeries);
                slotExercise.setProperty("protocolSeries", newProtocolSeries);
              }

              slotExercise.setProperty("series", []);
            }

            const initialLength = isPart ? slotExercise.series.length : 0;

            let match = "";
            let sets = 0;
            let reps = "";
            let rpe = "";
            let rampRpe = 0;
            let relevance = 3;
            // Hold weight progression list
            let weightList = [];
            // Hold RPE progression list
            let progressionList = [];
            // Normalize input: strip whitespace and lowercase
            const normalizedText = trimmedText
              .replace(/\s+/g, "")
              .toLowerCase();
            // Clamp RPE to 1–10
            const clampRpe = (val) => Math.min(Math.max(val, 1), 10);

            if (protocolCase === ProtocolCases.RPE_PROGRESSION) {
              // x<reps>@<rpe>-... progression, e.g. "x3@4-4-5"
              match = normalizedText.match(
                /^x(\d+)@((?:\d+(\.\d+)?)(?:-\d+(\.\d+)?)+)$/
              );
              reps = parseInt(match[1], 10);
              progressionList = match[2].split("-").map((n) => parseFloat(n));
              // Clamp RPE values to 1–10
              progressionList = progressionList.map((n) => clampRpe(n));
              sets = progressionList.length;
              // we'll use progressionList when building series
            } else if (protocolCase === ProtocolCases.WEIGHT_PROGRESSION) {
              // <w1>-<w2>-...x<reps>, e.g. "175-180-185x3"
              match = normalizedText.match(/^(\d+(?:-\d+)+)x(\d+)$/);
              reps = parseInt(match[2], 10);
              weightList = match[1]
                .split("-")
                .map((n) => Math.min(parseFloat(n), 999));
              sets = weightList.length;
              // No RPE for weight progression
              rpe = "";
            } else if (protocolCase === ProtocolCases.SETS_REPS_WEIGHT) {
              // <sets>x<reps>x<weight>, e.g. "3x5x145"
              // match = normalizedText.match(/^(\d+)x(\d+)x(\d+)$/);
              match = normalizedText.match(/^(\d+)x(\d+)x(\d+(?:\.\d+)?)$/);
              sets = parseInt(match[1], 10);
              reps = parseInt(match[2], 10);
              const rawWeight = parseFloat(match[3]);
              const weightValue = Math.min(rawWeight, 999);
              weightList = Array(sets).fill(weightValue);
              // No RPE for sets-reps-weight
              rpe = "";
            } else if (protocolCase === ProtocolCases.WEIGHT_REPS) {
              // <weight>x<reps>, e.g. "200x4"
              const match = normalizedText.match(/^(\d+)x(\d+)$/);
              reps = match[2];
              sets = 1;
            } else if (protocolCase === ProtocolCases.RAMP) {
              // ramp <sets>x<reps>@<rpe>, e.g. "ramp3x4@8"
              const match = normalizedText.match(
                /^ramp(\d+)x(\d+)@(\d+(\.\d+)?)/ // removed \s* and i flag
              );
              sets = parseInt(match[1], 10);
              reps = match[2];
              rpe = match[3];
              rampRpe = parseFloat(rpe);
              rampRpe = clampRpe(rampRpe);
            } else if (protocolCase === ProtocolCases.SETS_REPS_RPE) {
              // <sets>x<reps>@<rpe>, e.g. "3x4@5"
              const match = normalizedText.match(/^(\d+)x(\d+)@(\d+(\.\d+)?)/);
              sets = Math.max(parseInt(match[1], 10), 1);
              reps = match[2];
              rpe = clampRpe(parseFloat(match[3])).toString();
            } else if (protocolCase === ProtocolCases.SINGLE) {
              // single@<rpe>, e.g. "single@5"
              const match = normalizedText.match(/@(\d+(\.\d+)?)/);
              sets = 1;
              reps = 1;
              rpe = clampRpe(parseFloat(match[1])).toString();
              relevance = 2;
            } else if (protocolCase === ProtocolCases.REPS_AND_RPE) {
              // x<reps>@<rpe>, e.g. "x4@5"
              const match = normalizedText.match(/^x(\d+)@(\d+(\.\d+)?)/);
              sets = 1;
              reps = match[1];
              rpe = clampRpe(parseFloat(match[2])).toString();
              relevance = 1;
            } else {
              // unknown case: bail out
              console.warn("unknown case:", normalizedText);
              return;
            }

            // Now build series based on sets
            if (sets === 0) {
              // ensure at least one
              if (slotExercise.series.length < 19) {
                // console.error("No sets defined in protocol, adding one serie");
                const newSerie = new Serie(
                  undefined,
                  initialLength,
                  0,
                  0,
                  0,
                  reps,
                  protocolCase === ProtocolCases.RPE_PROGRESSION
                    ? progressionList[0]
                    : rpe,
                  protocolCase === ProtocolCases.WEIGHT_PROGRESSION ||
                  protocolCase === ProtocolCases.SETS_REPS_WEIGHT
                    ? weightList[0]
                    : "",
                  slotExercise,
                  protocolCase === ProtocolCases.RAMP
                    ? Math.min(sets, 3)
                    : protocolCase === ProtocolCases.SINGLE ||
                      protocolCase === ProtocolCases.REPS_AND_RPE
                    ? relevance
                    : 3
                );

                // For single or reps-and-rpe protocols, always require video
                if (
                  protocolCase === ProtocolCases.SINGLE ||
                  protocolCase === ProtocolCases.REPS_AND_RPE
                ) {
                  newSerie.setProperty("requireVideo", true);
                }
                slotExercise.series.push(newSerie);
                // Clear any prior protocol text error
              }
              return;
            }
            const capacity = 19 - slotExercise.series.length;
            const iterations = Math.min(sets, capacity);
            const startingRpe = rampRpe - (sets - 1);
            for (let i = 0; i < iterations; i++) {
              const newSerie = new Serie(
                undefined,
                initialLength + i,
                0,
                0,
                0,
                undefined,
                undefined,
                undefined,
                slotExercise,
                undefined
              );

              const rawRpe =
                protocolCase === ProtocolCases.RAMP
                  ? startingRpe + i
                  : protocolCase === ProtocolCases.RPE_PROGRESSION
                  ? progressionList[i]
                  : rpe;
              const serieRpe = clampRpe(rawRpe);
              const rawRelevance =
                protocolCase === ProtocolCases.RAMP
                  ? sets - i
                  : protocolCase === ProtocolCases.SINGLE ||
                    protocolCase === ProtocolCases.REPS_AND_RPE
                  ? relevance
                  : 3;
              const serieRelevance = Math.min(Math.max(rawRelevance, 1), 3);
              const repsValue = Math.min(reps, 999);
              //Check if a protocolSeries already exists
              if (initialLength + i < slotExercise?.protocolSeries?.length) {
                // console.log("Serie found for protocolCase", protocolCase);
                const foundSerie =
                  slotExercise.protocolSeries[initialLength + i];
                // If it exists, copy the serie properties
                if (foundSerie) {
                  newSerie.targetWeight = foundSerie.targetWeight;
                  newSerie.targetRpe = foundSerie.targetRpe;
                  newSerie.targetReps = foundSerie.targetReps;
                  newSerie.relevance = foundSerie.relevance;
                  newSerie.requireVideo = foundSerie.requireVideo;
                }
              } else {
                // console.log("No serie found for protocolCase", protocolCase);
                // For ramp protocols, require video on the final set (relevance=1)
                if (
                  (protocolCase === ProtocolCases.RAMP &&
                    serieRelevance === 1) ||
                  protocolCase === ProtocolCases.SINGLE ||
                  protocolCase === ProtocolCases.REPS_AND_RPE
                ) {
                  newSerie.requireVideo = true;
                }

                newSerie.relevance = serieRelevance;
              }

              if (reps && repsValue) {
                newSerie.targetReps = repsValue;
              }

              if (
                protocolCase === ProtocolCases.WEIGHT_PROGRESSION ||
                protocolCase === ProtocolCases.SETS_REPS_WEIGHT
              ) {
                newSerie.targetWeight = weightList[i];
              } else {
                newSerie.targetRpe = serieRpe;
              }

              slotExercise.series.push(newSerie);
            }
          };

          if (matchTest(ProtocolCases.RAMP, trimmedText)) {
            updateExercise(slotExercise, ProtocolCases.RAMP, trimmedText);
            const parts = text.split("+").map((part) => part.trim());
            parts.shift();
            parts.forEach((part) => processChainedSegment(slotExercise, part));
          } else if (matchTest(ProtocolCases.WEIGHT_PROGRESSION, trimmedText)) {
            updateExercise(
              slotExercise,
              ProtocolCases.WEIGHT_PROGRESSION,
              trimmedText
            );
            const parts = text.split("+").map((part) => part.trim());
            parts.shift();
            parts.forEach((part) => processChainedSegment(slotExercise, part));
          } else if (matchTest(ProtocolCases.SETS_REPS_WEIGHT, trimmedText)) {
            updateExercise(
              slotExercise,
              ProtocolCases.SETS_REPS_WEIGHT,
              trimmedText
            );
            const parts = text.split("+").map((part) => part.trim());
            parts.shift();
            parts.forEach((part) => processChainedSegment(slotExercise, part));
          } else if (matchTest(ProtocolCases.WEIGHT_REPS, trimmedText)) {
            updateExercise(
              slotExercise,
              ProtocolCases.WEIGHT_REPS,
              trimmedText
            );
            const parts = text.split("+").map((part) => part.trim());
            parts.shift();
            parts.forEach((part) => processChainedSegment(slotExercise, part));
          } else if (matchTest(ProtocolCases.RPE_PROGRESSION, trimmedText)) {
            updateExercise(
              slotExercise,
              ProtocolCases.RPE_PROGRESSION,
              trimmedText
            );
            const parts = text.split("+").map((part) => part.trim());
            parts.shift();
            parts.forEach((part) => processChainedSegment(slotExercise, part));
          } else if (matchTest(ProtocolCases.SETS_REPS_RPE, trimmedText)) {
            updateExercise(
              slotExercise,
              ProtocolCases.SETS_REPS_RPE,
              trimmedText
            );
            const parts = text.split("+").map((part) => part.trim());
            parts.shift();
            parts.forEach((part) => processChainedSegment(slotExercise, part));
          } else if (matchTest(ProtocolCases.SINGLE, trimmedText)) {
            updateExercise(slotExercise, ProtocolCases.SINGLE, trimmedText);
            const parts = text.split("+").map((part) => part.trim());
            parts.shift();
            parts.forEach((part) => processChainedSegment(slotExercise, part));
          } else if (matchTest(ProtocolCases.REPS_AND_RPE, trimmedText)) {
            updateExercise(
              slotExercise,
              ProtocolCases.REPS_AND_RPE,
              trimmedText
            );
            const parts = text.split("+").map((part) => part.trim());
            parts.shift();
            parts.forEach((part) => processChainedSegment(slotExercise, part));
          } else {
            console.warn("unknown case 1:", trimmedText);
          }
        };

        const handleKeyPress = (e) => {
          if (e.nativeEvent.key === "Enter" || e.nativeEvent.key === "Tab") {
            if (suggestion) {
              //Check if any of the series have values in actual
              const actualWithData = slotExercise.series.some(
                (s) => s.weight > 0 || s.rpe > 0 || s.reps > 0
              );

              if (actualWithData && !slotExercise.ignoreActualWithDataWarning) {
                slotExercise.setProperty("showActualWithDataWarning", true);
                return;
              }

              if (suggestion === " + " && e.nativeEvent.key === "Enter") {
                e.preventDefault();
                setShowingEdit(false);
                return;
              }

              addSuggestion(suggestion);

              if (textInputRef.current) {
                textInputRef.current.focus();
              }
            } else if (slotExercise.protocolText != "") {
              setShowingEdit(false);
            }
            e.preventDefault();
          }
        };

        return (
          <View
            style={{
              width: "100%",
              marginTop: slotExercise.isSecondaryProtocol ? 10 : 0,
            }}
          >
            <View
              style={{
                position: "relative",
                width: "100%",
                minHeight: 32,
              }}
              onMouseEnter={() => !isMobile && setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {/* View layer (always mounted) */}
              <View
                pointerEvents={showingEdit ? "none" : "auto"}
                style={{
                  position: "relative",
                  width: "100%",
                  padding: showingEdit ? 0 : 2,
                  minHeight: showingEdit ? 0 : 32,
                  justifyContent: "center",
                  opacity: showingEdit ? 0 : 1,
                  height: showingEdit ? 0 : undefined,
                  // overflow: "hidden",
                }}
              >
                <Text
                  numberOfLines={3}
                  onPress={() => {
                    setShowingEdit(true);
                    setShouldFocusAtEnd(true);
                  }}
                  style={{
                    width: "100%",
                    color: "gray",
                    textAlign: "center",
                    fontSize: screenSize === "small" ? 10 : 10,
                    fontWeight: isHovered ? "600" : "normal",
                    padding: 2,
                  }}
                >
                  {slotExercise.protocolText}
                </Text>

                {protocolTextError && (
                  <AntDesign
                    name="exclamationcircleo"
                    size={14}
                    color="red"
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      padding: 2,
                      opacity: 0.7,
                    }}
                    onPress={() => {
                      const coach = block?.client?.coach ?? block.coach;
                      coach.setProperty("showingProtocolsGuide", false);
                      setTimeout(() => {
                        coach.setProperty("showingProtocolsGuide", true);
                      }, 0);
                    }}
                  />
                )}
              </View>

              {/* Edit layer (always mounted) */}
              <View
                style={{
                  position: "relative",
                  width: "100%",
                  borderWidth: 0.5,
                  borderColor: isHovered
                    ? "rgba(0,0,0,0.5)"
                    : Colors.borderColor,
                  borderRadius: 8,
                  minHeight: 32,
                  padding: 4,
                  display: showingEdit ? "flex" : "none",
                }}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {/* Typing animation overlay - only visible when empty and week 1 */}
                {showingEdit &&
                  slotExercise.protocolText === "" &&
                  useCTAColors &&
                  !slotExercise.isSecondaryProtocol && (
                    <View
                      style={{
                        position: "absolute",
                        top: 4,
                        left: 4,
                        right: 4,
                        bottom: 4,
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        pointerEvents: "none",
                        zIndex: 0,
                      }}
                    >
                      <TypingAnimation
                        texts={[
                          "3x4 @5",
                          "175-180-185 x4",
                          "Ramp 3x4 @7 + Single @7",
                          "x4 @7 + 3x4 @5",
                          "x4 @4-5-6",
                          "3x5x200 + Single @8",
                        ]}
                        delay={80}
                        startDelay={300}
                        pauseBetween={2000}
                      />
                    </View>
                  )}

                {/* static display (with suggestion) behind the input */}
                <Text
                  style={{
                    fontSize: 11,
                    color: "transparent",
                    flexWrap: "wrap",
                    marginBottom: 2,
                  }}
                >
                  {slotExercise.protocolText}
                  {suggestion && (
                    <Text style={{ color: "grey" }}>{suggestion}</Text>
                  )}
                </Text>

                <TextInput
                  ref={textInputRef}
                  editable={showingEdit}
                  style={[
                    StyleSheet.absoluteFillObject,
                    {
                      top: 4,
                      left: 4,
                      right: 4,
                      bottom: 4,
                      color: "black",
                      fontSize: 11,
                      textAlignVertical: "top",
                      outline: "none",
                      backgroundColor: "transparent",
                      zIndex: 1,
                    },
                  ]}
                  value={slotExercise.protocolText}
                  selection={
                    showingEdit && shouldFocusAtEnd
                      ? {
                          start: slotExercise.protocolText.length,
                          end: slotExercise.protocolText.length,
                        }
                      : undefined
                  }
                  onChangeText={(text) => changeProtocol(text, slotExercise)}
                  autoCorrect={false}
                  autoCapitalize="none"
                  placeholderTextColor={Colors.grayColor}
                  multiline
                  onKeyPress={handleKeyPress}
                  blurOnSubmit={false}
                  maxLength={60}
                  onBlur={() => {
                    if (slotExercise.protocolText !== "") {
                      setShowingEdit(false);
                    }
                  }}
                  scrollEnabled={false}
                />
              </View>
            </View>
          </View>
        );
      });

      const EmptyTextBox = () => {
        const [isHovered, setIsHovered] = useState(false);

        return (
          <TouchableOpacity
            onPress={() => {
              if (openPrimarySearchRef.current) {
                openPrimarySearchRef.current(false);
              }
            }}
            style={{
              width: "100%",
              position: "relative",
              borderWidth: 0.5,
              borderColor: isHovered
                ? "rgba(0, 0, 0, 0.5)"
                : Colors.borderColor,
              borderRadius: 8,
              minHeight: 32,
              outlineStyle: "none",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          />
        );
      };

      return (
        <View
          style={{
            alignItems: "center",
            flex: 1,
          }}
        >
          {slotExercise ? (
            <ProtocolTextBox slotExercise={slotExercise} />
          ) : (
            <EmptyTextBox />
          )}

          {slotExercise && slotExercise.getSecondProtocol && (
            <ProtocolTextBox slotExercise={slotExercise.getSecondProtocol} />
          )}

          {showingProtocolDetectionAlert && (
            <CustomAlert
              isVisible={showingProtocolDetectionAlert}
              onClose={() => setShowingProtocolDetectionAlert(false)}
              title={"protocolDetectionTitle-string"}
              text={"protocolDetectionText-string"}
            />
          )}
          {slotExercise?.showActualWithDataWarning && (
            <CustomAlert
              isVisible={slotExercise?.showActualWithDataWarning}
              onClose={() =>
                slotExercise?.setProperty("showActualWithDataWarning", false)
              }
              title={"actualWithDataProtocolWarningTitle-string"}
              text={"actualWithDataProtocolWarningText-string"}
              action={() => {
                slotExercise?.setProperty("ignoreActualWithDataWarning", true);
                slotExercise?.setProperty("showActualWithDataWarning", false);
              }}
              specialCTA={"modify-string"}
            />
          )}
        </View>
      );
    });

    const checkCopyProtocolWarning = () => {
      if (!slotExercise)
        console.error(
          "No slotExercise has been passed as parameter of checkCopyProtocolWarning"
        );

      const useWarning = block.checkIfSeriesWithData(
        slotExercise.getWeek + 1,
        slotExercise.protocolSlot
      );
      if (useWarning) {
        setShowingExerciseWithDataWarning(true);
      } else {
        block.copyProtocol(slotExercise);
      }
    };

    return (
      <View
        style={{
          alignItems: "center",
          flexDirection: "row",
          minWidth: 264,
          minHeight: 32,
        }}
        onMouseEnter={() =>
          !isMobile &&
          slotExercise &&
          slotExercise.setProperty("isProtocolHovered", true)
        }
        onMouseLeave={() =>
          slotExercise && slotExercise.setProperty("isProtocolHovered", false)
        }
      >
        <View
          style={[
            GlobalStyles.HStack,
            {
              width: "100%",
              gap: 16,
            },
          ]}
        >
          <SlotTextNew
            slotExercise={slotExercise}
            selectSlot={selectSlot}
            useCTAColors={useCTAColors}
            calendar={calendar}
            block={block}
            protocolTitle={protocolTitle}
            week={week}
            onRegisterOpenSearch={(fn) => {
              openPrimarySearchRef.current = fn;
            }}
            onExerciseSetFinished={({ isSecondProtocol } = {}) => {
              const isSecondary = isSecondProtocol ? 1 : 0;
              const key = `${protocolTitle}:${week}:${isSecondary}`;
              const wasEditing = protocolTextEditModesRef.current.has(key);
              protocolTextFocusRequestsRef.current.set(key, { wasEditing });
              setFocusRequestVersion((v) => v + 1);
            }}
          />
          <ProtocolText />
        </View>
        <Observer>
          {() => (
            <>
              {slotExercise && week < block.totalWeeks && (
                <Tooltip
                  content="copySlotAndProtocol-string"
                  placement="top"
                  delayedOpen={true}
                >
                  <TouchableOpacity
                    onPress={checkCopyProtocolWarning}
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 8,
                      alignSelf: "center",
                      opacity:
                        slotExercise.isProtocolHovered || isMobile ? 1 : 0,
                      marginLeft: 8,
                      width: 24,
                      height: 32,
                      outlineStyle: "none",
                    }}
                  >
                    <Ionicons
                      name="arrow-forward-circle-outline"
                      size={20}
                      color={Colors.bubbleColor}
                    />
                  </TouchableOpacity>
                </Tooltip>
              )}
            </>
          )}
        </Observer>
        {showingExerciseWithDataWarning && (
          <CustomAlert
            isVisible={showingExerciseWithDataWarning}
            onClose={() => setShowingExerciseWithDataWarning(false)}
            title={"actualWithDataCopyProtocolWarningTitle-string"}
            text={t("actualWithDataCopyProtocolWarningText-string", {
              week: slotExercise?.getWeek + 1 ?? 1,
            })}
            action={() => {
              block.copyProtocol(slotExercise);
            }}
            specialCTA={"replace-string"}
          />
        )}
      </View>
    );
  });

  const SerieRowViewRework = ({ serie, idx, blockDashboardShowingActual }) => {
    const PropertyCases = {
      weight: "weight",
      reps: "reps",
      rpe: "rpe",
      targetWeight: "targetWeight",
      targetReps: "targetReps",
      targetRpe: "targetRpe",
    };

    const [refresh, setRefresh] = useState(false);
    useEffect(() => {
      const dispose = reaction(
        () => ({
          updateSets: serie.exercise.updateSets,
          updateSet: serie.updateSet,
        }),
        () => {
          setRefresh((prev) => !prev);
        }
      );

      return () => dispose();
    }, [serie.exercise]);

    const TextBox = ({ property, propertyCase }) => {
      const isTargetCase = [
        PropertyCases.targetWeight,
        PropertyCases.targetReps,
        PropertyCases.targetRpe,
      ].includes(propertyCase);

      const initialValue =
        typeof property === "string"
          ? property
          : property !== 0 && property != null
          ? formatNumber1Dec(property)
          : "";

      const [propertyString, setPropertyString] = useState(initialValue);
      const [isHovered, setIsHovered] = useState(false);
      const [isEditing, setIsEditing] = useState(false);
      const inputRef = useRef(null);

      useEffect(() => {
        setPropertyString(
          typeof property === "string"
            ? property
            : property !== 0 && property != null
            ? formatNumber1Dec(property)
            : ""
        );
      }, [refresh]);

      // useEffect(() => {
      //   if (isEditing && inputRef.current) {
      //     inputRef.current.focus();
      //   }
      // }, [isEditing]);

      const handleChange = (text) => {
        const formattedText = text.replace(",", ".");

        if (isTargetCase) {
          setPropertyString(formattedText);
          serie.setProperty(propertyCase, formattedText);
          return;
        }

        const aux = Number(formattedText);

        if (!isNaN(aux)) {
          setPropertyString(formattedText);

          let numAux;
          if (formattedText === "") {
            numAux = Number(formattedText);
          } else {
            numAux = Number(formattedText) + 0.0000001;
          }

          if (propertyCase === PropertyCases.weight) {
            serie.setProperty("weight", numAux);
          } else if (propertyCase === PropertyCases.reps) {
            serie.setProperty("reps", Math.round(numAux));
          } else if (propertyCase === PropertyCases.rpe) {
            serie.setProperty("rpe", numAux);
          }
        }
      };

      // const handleBlur = () => {
      //   setIsEditing(false);
      // };

      // const handleKeyPress = (e) => {
      //   if (e.nativeEvent.key === "Enter") {
      //     setIsEditing(false);
      //   }
      // };

      const getBackgroundColor = (serie) => {
        if (!serie) return "transparent";

        const target = serie.getTargetRpeReference;
        const actual = serie.rpe;

        if (typeof target === "number" && target > 0.02 && actual > 0.02) {
          // round both to one decimal (to match your formatNumber logic)
          const a = parseFloat(formatNumber1Dec(actual));
          const t = parseFloat(formatNumber1Dec(target));

          if (Math.abs(a - t) >= 1) {
            return a > t
              ? "rgba(240, 68, 56, 0.15)" // too heavy
              : "rgba(93, 196, 96, 0.15)"; // too light
          }
        }

        return "transparent";
      };

      const isActualEmpty =
        !isTargetCase &&
        (propertyString == null || propertyString.trim() === "");

      const effectiveBorderColor = isHovered
        ? isActualEmpty
          ? "rgb(200,200,200)"
          : "gray"
        : isActualEmpty
        ? Colors.borderColor
        : Colors.grayColor;

      // For "Actual" fields, render Text by default, swap to TextInput on tap/focus
      // if (!isTargetCase && !isEditing) {
      //   return (
      //     <View
      //       style={{
      //         borderColor: isHovered ? "gray" : Colors.grayColor,
      //         height: screenSize === "small" ? 24 : 24,
      //         borderRadius: screenSize === "small" ? 10 : 10,
      //         borderWidth: isHovered ? 1.2 : 1,
      //         justifyContent: "center",
      //         alignItems: "center",
      //         width: 48,
      //         backgroundColor:
      //           propertyCase === PropertyCases.rpe && getBackgroundColor(serie),
      //       }}
      //       onMouseEnter={() => !isMobile && setIsHovered(true)}
      //       onMouseLeave={() => setIsHovered(false)}
      //     >
      //       <TouchableOpacity
      //         style={{ width: "100%", height: 24, justifyContent: "center" }}
      //         onPress={() => setIsEditing(true)}
      //         activeOpacity={0.7}
      //       >
      //         <Text
      //           style={{
      //             width: "100%",
      //             textAlign: "center",
      //             color: "black",
      //             fontSize: screenSize === "small" ? 11 : 11,
      //           }}
      //           numberOfLines={1}
      //         >
      //           {propertyString}
      //         </Text>
      //       </TouchableOpacity>
      //     </View>
      //   );
      // }

      // Target fields, or "Actual" fields in edit mode
      return (
        <View
          style={{
            borderColor: effectiveBorderColor,
            height: screenSize === "small" ? 24 : 24,
            borderRadius: screenSize === "small" ? 10 : 10,
            borderWidth: isHovered ? 1.2 : 1,
            justifyContent: "center",
            alignItems: "center",
            width: 48,
            backgroundColor:
              propertyCase === PropertyCases.rpe && getBackgroundColor(serie),
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <TextInput
            ref={inputRef}
            value={propertyString}
            onChangeText={handleChange}
            inputMode={isTargetCase ? "text" : "numeric"}
            style={{
              width: "100%",
              height: 24,
              borderRadius: screenSize === "small" ? 10 : 10,
              textAlign: "center",
              color: "black",
              fontSize: screenSize === "small" ? 11 : 11,
            }}
            maxLength={10}
            // onBlur={handleBlur}
            // onKeyPress={handleKeyPress}
          />
        </View>
      );
    };

    const copyTargetToActual = () => {
      serie.copyTargetToActual();
      setRefresh(!refresh);
    };

    const duplicateSerieBelow = () => {
      serie.exercise.duplicateSerieBelow(serie);
    };

    const Circles = () => {
      const [circlesMenu, setCirclesMenu] = useState(false);
      const showCirclesMenu = () => setCirclesMenu(true);

      const handleMenuItemPress = (relevance) => {
        serie.setProperty("relevance", relevance);
        setCirclesMenu(false);
      };

      const [isHovered, setIsHovered] = useState(false);

      const MenuItemView = ({ option }) => {
        const [isHovered, setIsHovered] = useState(false);

        return option === 1 ? (
          <MenuItem
            onPress={() => handleMenuItemPress(1)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "red", borderWidth: 0.2 },
              ]}
            />

            <Text style={{ marginLeft: 6 }}>{t("highPriority-string")}</Text>
          </MenuItem>
        ) : option === 2 ? (
          <MenuItem
            onPress={() => handleMenuItemPress(2)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "yellow", borderWidth: 0.2 },
              ]}
            />

            <Text style={{ marginLeft: 6 }}>{t("mediumPriority-string")}</Text>
          </MenuItem>
        ) : (
          <MenuItem
            onPress={() => handleMenuItemPress(3)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "green", borderWidth: 0.2 },
              ]}
            />
            <Text style={{ marginLeft: 6 }}>{t("lowPriority-string")}</Text>
          </MenuItem>
        );
      };

      return (
        <Menu
          visible={circlesMenu}
          anchor={
            <Tooltip
              content="setPriority-string"
              placement="top"
              delayedOpen={true}
              disabled={circlesMenu}
            >
              <TouchableOpacity
                style={{ opacity: isHovered ? 0.5 : 1, outlineStyle: "none" }}
                onPress={showCirclesMenu}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <View
                  style={[
                    GlobalStyles.circle,
                    {
                      backgroundColor:
                        serie.relevance === 1
                          ? "red"
                          : serie.relevance === 2
                          ? "yellow"
                          : "green",
                      borderWidth: 0.2,
                    },
                  ]}
                />
              </TouchableOpacity>
            </Tooltip>
          }
          style={GlobalStyles.menu}
          onRequestClose={() => setCirclesMenu(false)}
        >
          <MenuItemView option={1} />
          <MenuItemView option={2} />
          <MenuItemView option={3} />
        </Menu>
      );
    };

    const VideoView = observer(() => {
      const [circlesMenu, setCirclesMenu] = useState(false);

      async function handleVideoPress() {
        if (serie.hasVideo) {
          const coach = block?.client?.coach;
          if (coach && !coach.isOffline) {
            const mediaManager = MediaManager.getInstance();
            mediaManager.showMediaPlayer(undefined, serie);
          }
        } else {
          if (serie.requireVideo) {
            serie.setProperty("requireVideo", false);
          } else {
            serie.setProperty("requireVideo", true);
          }
        }
      }

      const handleMenuItemPress = (relevance) => {
        serie.setProperty("relevance", relevance);
        setCirclesMenu(false);
      };

      const [isHovered, setIsHovered] = useState(false);

      const MenuItemView = ({ option }) => {
        const [isHovered, setIsHovered] = useState(false);

        return option === 1 ? (
          <MenuItem
            onPress={() => handleMenuItemPress(1)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "red", borderWidth: 0.2 },
              ]}
            />

            <Text style={{ marginLeft: 6 }}>{t("highPriority-string")}</Text>
          </MenuItem>
        ) : option === 2 ? (
          <MenuItem
            onPress={() => handleMenuItemPress(2)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "yellow", borderWidth: 0.2 },
              ]}
            />

            <Text style={{ marginLeft: 6 }}>{t("mediumPriority-string")}</Text>
          </MenuItem>
        ) : (
          <MenuItem
            onPress={() => handleMenuItemPress(3)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "green", borderWidth: 0.2 },
              ]}
            />
            <Text style={{ marginLeft: 6 }}>{t("lowPriority-string")}</Text>
          </MenuItem>
        );
      };

      return (
        <Menu
          visible={circlesMenu}
          anchor={
            <Tooltip
              content={
                serie.hasVideo
                  ? ""
                  : serie.requireVideo
                  ? "dontRequestVideo-string"
                  : "requestVideo-string"
              }
              placement="top"
              delayedOpen={true}
              disabled={serie.hasVideo}
            >
              <TouchableOpacity
                style={{ opacity: isHovered ? 0.5 : 1, outlineStyle: "none" }}
                onPress={handleVideoPress}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Ionicons
                  name={
                    serie.hasVideo
                      ? "videocam"
                      : serie.requireVideo
                      ? "videocam-outline"
                      : "videocam-off-outline"
                  }
                  size={screenSize === "large" ? 20 : 20}
                  color={
                    serie.hasVideo || serie.requireVideo
                      ? "black"
                      : "rgba(0, 0, 0, 0.2)"
                  }
                  style={{
                    marginLeft: 8,
                  }}
                />
              </TouchableOpacity>
            </Tooltip>
          }
          style={GlobalStyles.menu}
          onRequestClose={() => setCirclesMenu(false)}
        >
          <MenuItemView option={1} />
          <MenuItemView option={2} />
          <MenuItemView option={3} />
        </Menu>
      );
    });

    const CopyTargetButton = () => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <Tooltip
          content="copyTargetToActual-string"
          placement="top"
          delayedOpen={true}
        >
          <TouchableOpacity
            onPress={copyTargetToActual}
            style={{ width: 40, alignItems: "center", outlineStyle: "none" }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Ionicons
              name={
                isHovered
                  ? "arrow-forward-circle"
                  : "arrow-forward-circle-outline"
              }
              size={screenSize === "large" ? 24 : 24}
              color={Colors.bubbleColor}
            />
          </TouchableOpacity>
        </Tooltip>
      );
    };

    const DuplicateSerieBelowButton = () => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <Tooltip
          content="copyToNextSet-string"
          placement="top"
          delayedOpen={true}
        >
          <TouchableOpacity
            onPress={duplicateSerieBelow}
            style={{ opacity: isHovered ? 0.5 : 1, outlineStyle: "none" }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <MaterialCommunityIcons
              name="arrow-down-right"
              size={screenSize === "large" ? 20 : 20}
              color={Colors.bubbleColor}
              style={{
                marginRight: 4,
              }}
            />
          </TouchableOpacity>
        </Tooltip>
      );
    };

    const DeleteSerieButton = () => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <Tooltip content="deleteSet-string" placement="top" delayedOpen={true}>
          <TouchableOpacity
            onPress={() => serie.exercise.deleteSerie(serie)}
            style={{ opacity: isHovered ? 0.5 : 1, outlineStyle: "none" }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Feather
              name="x"
              size={screenSize == "large" ? 20 : 20}
              color={Colors.bubbleColor}
            />
          </TouchableOpacity>
        </Tooltip>
      );
    };

    return (
      <View>
        <View
          style={{
            width: "100%",
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "center",
            marginTop:
              idx !== 0 ? 4 : serie.exercise.isSecondaryProtocol ? 16 : 0,
            gap: showSetPriority ? 8 : blockDashboardShowingActual ? 10 : 13,
          }}
        >
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              width: showSetPriority ? 40 : 20,
              justifyContent: showSetPriority ? "center" : "flex-end",
            }}
          >
            {showSetPriority && <Circles />}
            <VideoView />
          </View>
          <TextBox
            property={serie.targetWeight}
            propertyCase={PropertyCases.targetWeight}
          />
          <TextBox
            property={serie.targetReps}
            propertyCase={PropertyCases.targetReps}
          />
          <TextBox
            property={serie.targetRpe}
            propertyCase={PropertyCases.targetRpe}
          />
          {blockDashboardShowingActual && (
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "center",
                gap: showSetPriority ? 8 : 10,
              }}
            >
              <CopyTargetButton />
              <TextBox
                property={serie.weight}
                propertyCase={PropertyCases.weight}
              />
              <TextBox
                property={serie.reps}
                propertyCase={PropertyCases.reps}
              />
              <TextBox property={serie.rpe} propertyCase={PropertyCases.rpe} />
            </View>
          )}
          <View
            style={{
              justifyContent: "space-around",
              flexDirection: "row",
              alignItems: "center",
              width: 48,
            }}
          >
            <DuplicateSerieBelowButton />
            <DeleteSerieButton />
          </View>
        </View>
      </View>
    );
  };

  const SlotOfWeekForSeries = observer(({ slotExercise }) => {
    const SerieHeader = observer(() => {
      const [isHovered, setIsHovered] = useState(false);
      const [isPlusHovered, setIsPlusHovered] = useState(false);
      const [isLinkHovered, setIsLinkHovered] = useState(false);
      const [isLinkFloatingOpen, setIsLinkFloatingOpen] = useState(false);

      const Header = ({ gap = 8, width = 160 }) => {
        return (
          <View
            style={{
              width: width,
              alignItems: "center",
              flexDirection: "row",
              gap: gap,
            }}
          >
            <Text
              style={{
                width: 48,
                textAlign: "center",
                fontSize: screenSize === "small" ? 11.5 : 11.5,
                fontWeight: "600",
                color: Colors.grayColor,
              }}
              numberOfLines={1}
            >
              {t("weight-string")}
            </Text>
            <Text
              style={{
                width: 48,
                textAlign: "center",
                fontSize: screenSize === "small" ? 11.5 : 11.5,
                fontWeight: "600",
                color: Colors.grayColor,
              }}
              numberOfLines={1}
            >
              REPS
            </Text>
            <Text
              style={{
                width: 48,
                textAlign: "center",
                fontSize: screenSize === "small" ? 11.5 : 11.5,
                fontWeight: "600",
                color: Colors.grayColor,
              }}
              numberOfLines={1}
            >
              {slotExercise?.getRirOrRpe ? "RIR" : "RPE"}
            </Text>
          </View>
        );
      };

      const AddSerieButton = () => {
        const [addSerieMenu, setAddSerieMenu] = useState(false);

        const MenuItemView = ({ option }) => {
          const [isHovered, setIsHovered] = useState(false);

          const handleMenuItemPress = (firstExercise) => {
            if (firstExercise) {
              slotExercise.addSerie();
            } else {
              slotExercise.getSecondProtocol.addSerie();
            }

            setAddSerieMenu(false);
          };
          return option === 0 ? (
            <MenuItem
              onPress={() => handleMenuItemPress(true)}
              style={{ minWidth: 160 }}
              textStyle={{
                fontSize: 14,
                color: Colors.blackColor,
                fontWeight: isHovered ? "600" : "normal",
              }}
              onMouseEnter={() => !isMobile && setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Text style={{}}>{t("firstExercise-string")}</Text>
            </MenuItem>
          ) : (
            option === 1 && (
              <MenuItem
                onPress={() => handleMenuItemPress(false)}
                style={{ minWidth: 160 }}
                textStyle={{
                  fontSize: 14,
                  color: Colors.blackColor,
                  fontWeight: isHovered ? "600" : "normal",
                }}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Text style={{}}>{t("secondExercise-string")}</Text>
              </MenuItem>
            )
          );
        };
        return !slotExercise.getSecondProtocol ? (
          <Tooltip content="addSet-string" placement="top" delayedOpen={true}>
            <TouchableOpacity
              style={{
                marginLeft: -1,
                opacity: isHovered || isMobile ? (isPlusHovered ? 0.6 : 1) : 0,
              }}
              onPress={() => {
                slotExercise?.addSerie();
              }}
              onMouseEnter={() => !isMobile && setIsPlusHovered(true)}
              onMouseLeave={() => setIsPlusHovered(false)}
            >
              <FontAwesome6 name="plus" size={13} color={Colors.grayColor} />
            </TouchableOpacity>
          </Tooltip>
        ) : (
          <Menu
            visible={addSerieMenu}
            anchor={
              <Tooltip
                content="addSet-string"
                placement="top"
                delayedOpen={true}
                disabled={addSerieMenu}
              >
                <TouchableOpacity
                  style={{
                    width: 16,
                    marginLeft: -1,
                    opacity:
                      isHovered || isMobile ? (isPlusHovered ? 0.6 : 1) : 0,
                  }}
                  onPress={() => setAddSerieMenu(true)}
                  // onMouseEnter={() => !isMobile && setIsPlusHovered(true)}
                  // onMouseLeave={() => setIsPlusHovered(false)}
                >
                  <FontAwesome6
                    name="plus"
                    size={13}
                    color={Colors.grayColor}
                  />
                </TouchableOpacity>
              </Tooltip>
            }
            style={GlobalStyles.menu}
            onRequestClose={() => setAddSerieMenu(false)}
          >
            <MenuItemView option={0} />
            <MenuItemView option={1} />
          </Menu>
        );
      };

      const selectedExerciseRef = useRef(slotExercise);

      const ExerciseLinkButton = () => {
        const [linkMenu, setLinkMenu] = useState(false);

        const MenuItemView = ({ option }) => {
          const [isHovered, setIsHovered] = useState(false);

          const handleMenuItemPress = (firstExercise) => {
            const targetExercise = firstExercise
              ? slotExercise
              : slotExercise.getSecondProtocol;

            selectedExerciseRef.current = targetExercise;
            setLinkMenu(false);
            setTimeout(() => {
              setIsLinkFloatingOpen(true);
            }, 50);
          };

          return option === 0 ? (
            <MenuItem
              onPress={() => handleMenuItemPress(true)}
              style={{ minWidth: 160 }}
              textStyle={{
                fontSize: 14,
                color: Colors.blackColor,
                fontWeight: isHovered ? "600" : "normal",
              }}
              onMouseEnter={() => !isMobile && setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Text style={{}}>{t("firstExercise-string")}</Text>
            </MenuItem>
          ) : (
            option === 1 && (
              <MenuItem
                onPress={() => handleMenuItemPress(false)}
                style={{ minWidth: 160 }}
                textStyle={{
                  fontSize: 14,
                  color: Colors.blackColor,
                  fontWeight: isHovered ? "600" : "normal",
                }}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Text style={{}}>{t("secondExercise-string")}</Text>
              </MenuItem>
            )
          );
        };

        return !slotExercise.getSecondProtocol ? (
          <FloatingExerciseLink
            exercise={slotExercise}
            isOpen={isLinkFloatingOpen}
            onOpenChange={setIsLinkFloatingOpen}
            onClose={() => {
              setIsLinkHovered(false);
              setIsLinkFloatingOpen(false);
            }}
          >
            <Tooltip
              content="addVideoLink-string"
              placement="top"
              delayedOpen={true}
              disabled={isLinkFloatingOpen}
            >
              <View
                style={{
                  opacity:
                    isHovered || isMobile ? (isLinkHovered ? 0.6 : 1) : 0,
                }}
                onMouseEnter={() => !isMobile && setIsLinkHovered(true)}
                onMouseLeave={() => setIsLinkHovered(false)}
              >
                <Observer>
                  {() => (
                    <Feather
                      name="link"
                      size={13}
                      color={
                        slotExercise.link
                          ? Colors.bubbleColor
                          : Colors.grayColor
                      }
                    />
                  )}
                </Observer>
              </View>
            </Tooltip>
          </FloatingExerciseLink>
        ) : (
          <>
            <Menu
              visible={linkMenu}
              anchor={
                <Tooltip
                  content="addVideoLink-string"
                  placement="top"
                  delayedOpen={true}
                  disabled={linkMenu}
                >
                  <TouchableOpacity
                    style={{
                      marginLeft: -4,
                      opacity:
                        isHovered || isMobile ? (isLinkHovered ? 0.6 : 1) : 0,
                    }}
                    onPress={() => {
                      console.log("Link button pressed, showing menu");
                      setLinkMenu(true);
                    }}
                  >
                    <Observer>
                      {() => (
                        <Feather
                          name="link"
                          size={13}
                          color={
                            slotExercise.link ||
                            slotExercise.getSecondProtocol.link
                              ? Colors.bubbleColor
                              : Colors.grayColor
                          }
                        />
                      )}
                    </Observer>
                  </TouchableOpacity>
                </Tooltip>
              }
              style={GlobalStyles.menu}
              onRequestClose={() => setLinkMenu(false)}
            >
              <MenuItemView option={0} />
              <MenuItemView option={1} />
            </Menu>
            <FloatingExerciseLink
              exercise={selectedExerciseRef.current}
              isOpen={isLinkFloatingOpen}
              onOpenChange={setIsLinkFloatingOpen}
              onClose={() => {
                setIsLinkHovered(false);
                setIsLinkFloatingOpen(false);
              }}
            >
              <View style={{ display: "none" }} />
            </FloatingExerciseLink>
          </>
        );
      };

      return (
        <View
          style={{
            width: "100%",
            alignItems: "center",
            flexDirection: "row",
            justifyContent: showSetPriority ? "center" : "flex-start",
            gap: showSetPriority
              ? 8
              : slotExercise?.getBlockDashboardShowingActual
              ? 10
              : 13,
            marginTop: 16,
            marginBottom: 4,
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => !isLinkFloatingOpen && setIsHovered(false)}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: showSetPriority ? "center" : "flex-start",
              width: showSetPriority ? 40 : 20,
              gap: 6,
            }}
          >
            <AddSerieButton />
            <ExerciseLinkButton />
          </View>

          <Header
            gap={
              showSetPriority
                ? 8
                : slotExercise?.getBlockDashboardShowingActual
                ? 10
                : 13
            }
            width={
              showSetPriority
                ? 160
                : slotExercise?.getBlockDashboardShowingActual
                ? 164
                : 170
            }
          />
          {slotExercise?.getBlockDashboardShowingActual && (
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                justifyContent: showSetPriority ? "center" : "flex-start",
                gap: showSetPriority ? 8 : 10,
              }}
            >
              <View style={{ width: 40 }} />
              <Header
                gap={showSetPriority ? 8 : 10}
                width={showSetPriority ? 160 : 164}
              />
            </View>
          )}

          <View style={{ width: 48 }} />
        </View>
      );
    });

    const TextNotes = ({ exercise }) => {
      const [string, setString] = useState(
        exercise.notes ? exercise.notes : ""
      );
      const [refresh, setRefresh] = useState(false);
      const [showingEdit, setShowingEdit] = useState(false);
      const [shouldFocusAtEnd, setShouldFocusAtEnd] = useState(false);

      const [isHovered, setIsHovered] = useState(false);
      const textInputRef = useRef();

      useEffect(() => {
        setString(exercise.notes ? exercise.notes : "");
      }, [refresh]);

      useEffect(() => {
        const dispose = reaction(
          () => ({
            updateSets: exercise.updateSets,
          }),
          () => {
            setRefresh((prev) => !prev);
          }
        );

        return () => dispose();
      }, []);

      useEffect(() => {
        if (showingEdit && shouldFocusAtEnd && textInputRef.current) {
          setTimeout(() => {
            textInputRef.current?.focus();
            // Reset the flag after focusing
            setShouldFocusAtEnd(false);
          }, 100);
        }
      }, [showingEdit, shouldFocusAtEnd]);

      const handleChange = (text) => {
        setString(text);
        exercise.setProperty("notes", text);
      };

      return (
        <View
          style={[
            GlobalStyles.HStack,
            {
              flex: 1,
              flexShrink: 1,
              marginTop: 12,
              alignItems: "flex-start",
              justifyContent: "flex-start",
              alignSelf: "flex-start",
              width: columnWidth,
            },
          ]}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={{
              position: "relative",
              width: "100%",
              minHeight: showingEdit ? 52 : 32,
              flex: 1,
            }}
          >
            {/* View layer (always mounted) */}
            <TouchableOpacity
              pointerEvents={showingEdit ? "none" : "auto"}
              style={{
                flex: 1,
                outlineStyle: "none",
                opacity: showingEdit ? 0 : 1,
              }}
              onPress={() => {
                setShowingEdit(true);
                setShouldFocusAtEnd(true);
              }}
            >
              <Text
                numberOfLines={3}
                style={{
                  alignSelf: "flex-start",
                  color: "gray",
                  fontSize: screenSize === "small" ? 10 : 10,
                  fontWeight: isHovered ? "600" : "normal",
                }}
              >
                {string != "" ? string : t("notes-string") + "..."}
              </Text>
            </TouchableOpacity>

            {/* Edit layer (always mounted) */}
            <View
              pointerEvents={showingEdit ? "auto" : "none"}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                borderWidth: 0.5,
                borderColor: isHovered ? "rgba(0,0,0,0.5)" : Colors.borderColor,
                borderRadius: 8,
                minHeight: 32,
                overflow: "hidden",
                opacity: showingEdit ? 1 : 0,
              }}
            >
              <TextInput
                ref={textInputRef}
                editable={showingEdit}
                value={string}
                onChangeText={handleChange}
                multiline
                numberOfLines={3}
                selection={
                  showingEdit && shouldFocusAtEnd
                    ? {
                        start: string.length,
                        end: string.length,
                      }
                    : undefined
                }
                style={{
                  fontSize: 11,
                  paddingVertical: 6,
                  paddingHorizontal: 8,
                  flex: 1,
                  outline: "none",
                }}
                onBlur={() => {
                  setShowingEdit(false);
                }}
              />
            </View>
          </View>
        </View>
      );
    };

    return (
      <View style={{ alignItems: "flex-start", minWidth: 264, minHeight: 32 }}>
        <SerieHeader />
        {slotExercise?.series?.map((serie, idx) => (
          <SerieRowViewRework
            key={serie.id}
            serie={serie}
            idx={idx}
            blockDashboardShowingActual={
              slotExercise?.getBlockDashboardShowingActual
            }
          />
        ))}
        {slotExercise?.series?.length > 0 && (
          <TextNotes exercise={slotExercise} />
        )}
        {slotExercise?.getSecondProtocol?.series?.map((serie, idx) => (
          <SerieRowViewRework
            key={serie.id}
            serie={serie}
            idx={idx}
            blockDashboardShowingActual={
              slotExercise?.getBlockDashboardShowingActual
            }
          />
        ))}
        {slotExercise?.getSecondProtocol?.series?.length > 0 && (
          <TextNotes exercise={slotExercise.getSecondProtocol} />
        )}
      </View>
    );
  });

  return (
    <View
      style={{
        marginTop:
          protocolTitle === ProtocolTitles.PRIMARY_SQUAT ||
          protocolTitle === ProtocolTitles.PRIMARY_BENCH_PRESS ||
          protocolTitle === ProtocolTitles.PRIMARY_DEADLIFT
            ? 0
            : 16,
        width: "100%",
        alignItems: "center",
      }}
    >
      <View
        style={{
          width: "100%",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <View
          style={{
            width: 180,
            textAlign: "center",
            paddingHorizontal: 16,
          }}
        >
          <ProtocolRowTitleToggle
            block={block}
            protocolTitle={protocolTitle}
            calendar={calendar}
          />
        </View>
        <Observer>
          {() => {
            const totalWeeks = block.totalWeeks || 0;
            const { start = 0, end = Math.max(0, totalWeeks - 1) } =
              visibleRange || {};
            const colWidth = columnWidth;
            const weeks = [];
            for (let w = start; w <= end; w++) {
              weeks.push(w + 1);
            }

            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  // borderWidth: 10,
                }}
              >
                {/* Left side: show one skeleton per offscreen week before the visible range */}
                {Array.from({ length: Math.max(0, start) }).map((_, i) => {
                  const w = i + 1; // week number for left skeletons
                  return (
                    <View
                      key={`left-skel-${w}`}
                      style={{
                        marginRight: w !== totalWeeks ? 40 : 16,
                        width: colWidth,
                      }}
                    >
                      <SlotOfWeekSkeleton />
                    </View>
                  );
                })}
                {weeks.map((week) => (
                  <View
                    style={{
                      marginRight: week !== totalWeeks ? 40 : 16,
                      width: colWidth,
                    }}
                    key={week}
                  >
                    {(() => {
                      const slot = block.getSlotOfWeek(protocolTitle, week);
                      return <SlotOfWeek slotExercise={slot} week={week} />;
                    })()}
                  </View>
                ))}
                {/* Right side: show one skeleton per offscreen week after the visible range */}
                {Array.from({
                  length: Math.max(0, totalWeeks - (end + 1)),
                }).map((_, i) => {
                  const w = end + 2 + i; // actual week index after visible range
                  return (
                    <View
                      key={`right-skel-${w}`}
                      style={{
                        marginRight: w !== totalWeeks ? 40 : 16,
                        width: colWidth,
                      }}
                    >
                      <SlotOfWeekSkeleton />
                    </View>
                  );
                })}
              </View>
            );
          }}
        </Observer>
      </View>

      <Observer>
        {() =>
          block.getProtocolSeriesShown(protocolTitle) && (
            <View
              style={{
                width: "100%",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <View
                style={{
                  width: 180,
                  textAlign: "center",
                  paddingHorizontal: 8,
                }}
              />
              <Observer>
                {() => {
                  const totalWeeks = block.totalWeeks || 0;
                  const { start = 0, end = Math.max(0, totalWeeks - 1) } =
                    visibleRange || {};
                  const colWidth = columnWidth;
                  const weeks = [];
                  for (let w = start; w <= end; w++) weeks.push(w + 1);
                  let leftSpacer = 0;
                  if (start > 0) {
                    if (start < totalWeeks - 1) {
                      leftSpacer = start * (colWidth + 40);
                    } else if (start === totalWeeks - 1) {
                      leftSpacer = (totalWeeks - 1) * (colWidth + 40);
                    }
                  }
                  let rightSpacer = 0;
                  if (end < totalWeeks - 1) {
                    rightSpacer =
                      (totalWeeks - (end + 2)) * (colWidth + 40) +
                      (colWidth + 16);
                  }
                  return (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "flex-start",
                      }}
                    >
                      {leftSpacer > 0 && (
                        <View
                          style={{
                            width: leftSpacer,
                            height: 1,
                          }}
                        />
                      )}
                      {weeks.map((week) => {
                        const slot = block.getSlotOfWeek(protocolTitle, week);
                        return (
                          <View
                            style={{
                              minWidth: colWidth,
                              marginRight: week !== totalWeeks ? 40 : 16,
                              alignSelf: "flex-start",
                            }}
                            key={week}
                          >
                            {slot?.series?.length > 0 && (
                              <SlotOfWeekForSeries slotExercise={slot} />
                            )}
                          </View>
                        );
                      })}
                      {/* Always render right spacer for debug, even if width 0 */}
                      {rightSpacer > 0 && (
                        <View
                          style={{
                            width: rightSpacer,
                            height: 1,
                          }}
                        />
                      )}
                    </View>
                  );
                }}
              </Observer>
            </View>
          )
        }
      </Observer>
    </View>
  );
};

export default ProtocolRow;
